import { getEnvByHostname } from '../utils/env';
import React, { useState } from 'react';
import { Card, Form, Input, Button, Space, message, Descriptions, Tag, Empty, Row, Col, Statistic, Timeline } from 'antd';
import { SearchOutlined, ReloadOutlined, UserOutlined, BankOutlined, InfoCircleOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';
import { testApi } from '../services/api';
import ReactJson from '@microlink/react-json-view';
import dayjs from 'dayjs';

export default function AccountQuery() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const { cache, save: saveCache } = usePageCache('accountQuery');

  React.useEffect(() => {
    if (cache) {
      if (cache.accountData) setAccountData(cache.accountData);
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    }
  }, []);
  React.useEffect(() => {
    return () => { saveCache({ accountData, formValues: form.getFieldsValue() }); };
  });

  const handleQuery = async (values) => {
    setLoading(true);
    try {
      const username = localStorage.getItem('username');
      const requestBody = { accountId: values.accountId };
      requestBody.requestId = values.requestId || `req_${Date.now()}`;
      const { data } = await testApi.executeTest({
        businessPartner: 'DEMO_ACCOUNT', env: getEnvByHostname(),
        testType: 'QUERY', requestBody: JSON.stringify(requestBody), executor: username
      });
      let responseData = data;
      if (data.data?.responseBody && typeof data.data.responseBody === 'string') {
        try { responseData = JSON.parse(data.data.responseBody); } catch (e) { responseData = data; }
      }
      if (responseData.code === '200' || responseData.code === 200) {
        setAccountData(responseData);
      } else {
        message.error(`${t('queryFailed')}: ${responseData.msg || t('queryFailed')}`);
        setAccountData(null);
      }
    } catch (error) {
      message.error(error.response?.data?.msg || t('queryFailed'));
    } finally { setLoading(false); }
  };

  const handleReset = () => { form.resetFields(); setAccountData(null); };

  const getStatusTag = (status) => {
    const cfg = { SUCCESS: { color: '#52c41a', icon: <CheckCircleOutlined /> }, ACTIVE: { color: '#52c41a', icon: <CheckCircleOutlined /> }, PENDING: { color: '#faad14', icon: <ClockCircleOutlined /> }, FAILED: { color: '#ff4d4f', icon: <InfoCircleOutlined /> } };
    const c = cfg[status] || cfg.PENDING;
    return <Tag color={c.color} icon={c.icon} style={{ fontSize: 14, padding: '4px 12px' }}>{status}</Tag>;
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div className="fade-in-up">
        <Card className="modern-card"
          title={<Space size={16}><SearchOutlined style={{ color: '#667eea', fontSize: 20 }} /><span style={{ fontSize: 20, fontWeight: 600 }}>{t('accountQuery')}</span></Space>}
          extra={<Button icon={<ReloadOutlined />} onClick={handleReset} type="text">{t('reset')}</Button>}
          style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical" onFinish={handleQuery} size="large">
            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 500 }}>{t('accountId')}</span>} name="accountId"
                  rules={[{ required: true, message: t('pleaseInput', t('accountId')) }]}>
                  <Input placeholder="e.g. AC1102038921" prefix={<UserOutlined style={{ color: '#9ca3af' }} />} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontWeight: 500 }}>Request ID ({t('optional')})</span>} name="requestId">
                  <Input placeholder={t('autoGenerate')} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} block className="primary-button" style={{ height: 48 }}>
                {t('search')}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {accountData?.data ? (
          <div className="fade-in-up">
            <Row gutter={16} style={{ marginBottom: 24 }}>
              {[
                { title: t('accountStatus'), value: accountData.data.status, icon: <CheckCircleOutlined style={{ color: '#667eea' }} />, fontSize: 20 },
                { title: t('channel'), value: accountData.data.channelCode, icon: <BankOutlined style={{ color: '#667eea' }} />, fontSize: 16 },
                { title: t('currency'), value: accountData.data.currency, fontSize: 24 },
                { title: t('paymentMethod'), value: accountData.data.paymentMethod, fontSize: 14 },
              ].map((s, i) => (
                <Col xs={24} sm={12} md={6} key={i}>
                  <Card className="modern-card" style={{ background: '#fff', borderLeft: '4px solid #667eea' }}>
                    <Statistic title={<span style={{ color: '#6b7280', fontSize: 14 }}>{s.title}</span>}
                      value={s.value} valueStyle={{ color: '#1f2937', fontSize: s.fontSize, fontWeight: 600 }} prefix={s.icon} />
                  </Card>
                </Col>
              ))}
            </Row>

            <Card className="modern-card"
              title={<Space><UserOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('accountDetail')}</span>{getStatusTag(accountData.data.status)}</Space>}
              style={{ marginBottom: 24 }}>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="middle">
                <Descriptions.Item label={t('accountId')} span={2}>
                  <code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4, fontSize: 14 }}>{accountData.data.accountId}</code>
                </Descriptions.Item>
                <Descriptions.Item label={t('status')}>{getStatusTag(accountData.data.status)}</Descriptions.Item>
                {accountData.data.errorMsg && (
                  <Descriptions.Item label={t('errorDetail')} span={3}>
                    <pre style={{ margin: 0, padding: '8px 12px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, fontSize: 13, color: '#ff4d4f', fontFamily: 'Monaco, Consolas, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 200, overflow: 'auto' }}>
                      {(() => { try { return JSON.stringify(JSON.parse(accountData.data.errorMsg), null, 2); } catch (e) { return accountData.data.errorMsg; } })()}
                    </pre>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t('externalUserId')}><code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{accountData.data.externalUserId}</code></Descriptions.Item>
                <Descriptions.Item label={t('channelCode')}><Tag color="blue">{accountData.data.channelCode}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('paymentMethod')}><Tag color="green">{accountData.data.paymentMethod}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('currency')}><Tag color="orange">{accountData.data.currency}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('createTime')}>{accountData.data.createTime ? dayjs(Number(accountData.data.createTime)).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
                <Descriptions.Item label={t('updateTime')}>{accountData.data.updateTime ? dayjs(Number(accountData.data.updateTime)).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
              </Descriptions>
            </Card>

            {accountData.data.accountDetail && (
              <Card className="modern-card"
                title={<Space><BankOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('bankAccountInfo2')}</span></Space>}
                style={{ marginBottom: 24 }}>
                <Row gutter={[24, 24]}>
                  <Col xs={24} md={12}>
                    <Descriptions column={1} size="middle">
                      <Descriptions.Item label={<span style={{ fontWeight: 500 }}>{t('accountName')}</span>}><span style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>{accountData.data.accountDetail.accountName}</span></Descriptions.Item>
                      <Descriptions.Item label={<span style={{ fontWeight: 500 }}>{t('accountNumber')}</span>}><code style={{ background: '#f5f5f5', padding: '6px 12px', borderRadius: 6, fontSize: 16, fontWeight: 600, letterSpacing: 1 }}>{accountData.data.accountDetail.accountNumber}</code></Descriptions.Item>
                      <Descriptions.Item label={<span style={{ fontWeight: 500 }}>{t('bic')}</span>}><Tag color="processing" style={{ fontSize: 14, padding: '4px 12px' }}>{accountData.data.accountDetail.bic}</Tag></Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col xs={24} md={12}>
                    <Descriptions column={1} size="middle">
                      <Descriptions.Item label={<span style={{ fontWeight: 500 }}>{t('bankName')}</span>}><span style={{ fontSize: 16, fontWeight: 600, color: '#1f2937' }}>{accountData.data.accountDetail.bankName}</span></Descriptions.Item>
                      <Descriptions.Item label={<span style={{ fontWeight: 500 }}>{t('bankCountry')}</span>}><Tag color="blue" style={{ fontSize: 14 }}>{accountData.data.accountDetail.bankCountry}</Tag></Descriptions.Item>
                      <Descriptions.Item label={<span style={{ fontWeight: 500 }}>{t('bankAccountInfo')}</span>}><span style={{ color: '#6b7280' }}>{accountData.data.accountDetail.bankAddress}</span></Descriptions.Item>
                    </Descriptions>
                  </Col>
                </Row>
              </Card>
            )}

            {accountData.data.extParam && (
              <Card className="modern-card" style={{ marginBottom: 24 }}
                title={<Space><InfoCircleOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('extParams') || '扩展参数'}</span></Space>}>
                <ReactJson
                  src={typeof accountData.data.extParam === 'string' ? (() => { try { return JSON.parse(accountData.data.extParam); } catch(e) { return { raw: accountData.data.extParam }; } })() : accountData.data.extParam}
                  collapsed={false}
                  theme="rjv-default"
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                  style={{ fontSize: 13 }}
                />
              </Card>
            )}

            <Card className="modern-card" title={<Space><ClockCircleOutlined style={{ color: '#667eea' }} /><span style={{ fontSize: 18, fontWeight: 600 }}>{t('operationTimeline')}</span></Space>}>
              <Timeline items={[
                { color: 'green', children: <div><div style={{ fontWeight: 600, marginBottom: 4 }}>{t('accountUpdated')}</div><div style={{ color: '#6b7280', fontSize: 14 }}>{accountData.data.updateTime ? dayjs(Number(accountData.data.updateTime)).format('YYYY-MM-DD HH:mm:ss') : '-'}</div></div> },
                { color: 'blue', children: <div><div style={{ fontWeight: 600, marginBottom: 4 }}>{t('accountCreated')}</div><div style={{ color: '#6b7280', fontSize: 14 }}>{accountData.data.createTime ? dayjs(Number(accountData.data.createTime)).format('YYYY-MM-DD HH:mm:ss') : '-'}</div></div> },
              ]} />
            </Card>
          </div>
        ) : (
          <Card className="modern-card">
            <Empty description={<span style={{ color: '#6b7280', fontSize: 16 }}>{t('enterAccountId')}</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Card>
        )}
      </div>
    </div>
  );
}
